import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../components/ui';
import { analytics, EventTypes } from '../../utils/analytics';
import { User, useUser } from '../../hooks/api/useUser';
import { Checkbox, Modal } from '@mui/material';
import { CloseIcon } from '../../components/common/svgs/svgs';
import moment from 'moment';
import { useHubspotEventMutation } from '../../hooks/api/useEvent';
import useAuth from '../../hooks/utils/useAuth';
import {
  getMemberPrivacyKey,
  getMemberTermsKey,
  useAcceptVersionedRecord,
  useVersionedRecord,
  useVersionedRecordAcceptance,
} from '../../hooks/api/useVersionedRecords';
import { resolveTheme } from '../../themes';
import { OnboardingLayout } from './common/layout';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATH } from '../../routes/route-paths';

export const SetPassword: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { updateUser } = useUser();
  const theme = resolveTheme();
  const memberTermsKey = getMemberTermsKey(theme.id);
  const memberPrivacyKey = getMemberPrivacyKey(theme.id);
  const { data: termsOfService } = useVersionedRecord(memberTermsKey);
  const { data: privacyPolicy } = useVersionedRecord(memberPrivacyKey);
  const { data: termsOfServiceAccepted } = useVersionedRecordAcceptance(memberTermsKey);
  const { data: privacyPolicyAccepted } = useVersionedRecordAcceptance(memberPrivacyKey);
  const { acceptVersionedRecord } = useAcceptVersionedRecord();
  const hubspotMutation = useHubspotEventMutation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [termsConditionsChecked, setTermsConditionsChecked] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [modal, setModal] = useState<'t&c' | 'privacy' | undefined>();
  const [skipPassword, setSkipPassword] = useState(false);
  const isSubmittingRef = useRef(false);
  const skipTermsAndPrivacy = termsOfServiceAccepted?.accepted && privacyPolicyAccepted?.accepted;

  const handleCheckboxChange = () => {
    setTermsConditionsChecked(!termsConditionsChecked);
  };

  const validatePassword = () => {
    if (skipPassword) return;

    if (confirmPassword !== '') {
      validateConfirmPassword();
    }

    if (password.length < 8 || password.length > 64) {
      setPasswordError('Password must be between 8 and 64 characters long');
      return false;
    }

    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = () => {
    if (skipPassword) return;

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords must match');
      return false;
    } else {
      setConfirmPasswordError('');
      return true;
    }
  };

  const handleContinue = async () => {
    // Prevent double submission
    if (isSubmittingRef.current || updateUser.isLoading) {
      return;
    }

    const isPasswordValid = validatePassword();
    const isConfirmPasswordValid = validateConfirmPassword();

    if (isPasswordValid === false || isConfirmPasswordValid === false || !termsOfService || !privacyPolicy) {
      return;
    }

    if (!user?.id) {
      setPasswordError('Your account is still loading. Please try again in a moment.');
      return;
    }

    isSubmittingRef.current = true;
    setShowPassword(false);

    try {
      await updateUser.mutateAsync({
        password: skipPassword ? undefined : password,
        caller_role: {
          accepts_marketing_emails: messaging,
          notifications_listener_online: messaging,
        },
      } as Partial<User>);

      const age = Math.floor(moment().diff(user?.date_of_birth, 'years') / 10) * 10;
      hubspotMutation.mutate({
        event: 'onboarding_started',
        properties: {
          package_code: user?.caller_role.active_subscription?.package.code!,
        },
      });
      analytics.trackEvent(
        EventTypes.STARTED_ONBOARDING,
        user?.client_name,
        user?.gender,
        // getting the nearest decade
        // check if the user has a date of birth before calculating age
        age > 150 ? undefined : age,
      );
      acceptVersionedRecord.mutate({
        key: memberTermsKey,
        versionId: termsOfService?.id,
      });
      acceptVersionedRecord.mutate({
        key: memberPrivacyKey,
        versionId: privacyPolicy?.id,
      });
      history.push(ROUTE_PATH.ONBOARDING_PROFILE);
    } catch (error: any) {
      if (error?.response?.data?.description) {
        setPasswordError(error.response.data.description);
      } else if (error?.message === 'User profile is not loaded yet') {
        setPasswordError('Your account is still loading. Please try again in a moment.');
      } else {
        setPasswordError('');
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    if (user?.is_sso || !user?.needs_password) {
      setSkipPassword(true);
    }
  }, [user]);

  /* if the user doesn't need to set a password, and has already accepted the terms and service,
  and privacy policy, then we can skip this page. This situation can happen if a user stared onboarding
  but didn't complete it. */
  useEffect(() => {
    if (skipPassword && skipTermsAndPrivacy) {
      history.push(ROUTE_PATH.ONBOARDING_PROFILE);
    }
  }, [skipPassword, skipTermsAndPrivacy, history]);

  useEffect(() => {
    if (password !== '') {
      validatePassword();
    }
    if (confirmPassword !== '') {
      validateConfirmPassword();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, confirmPassword]);

  return (
    <OnboardingLayout>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
        noValidate
      >
        <div className="space-y-6">
          {!skipPassword && (
            <div className="space-y-1">
              <h1 className="font-albert-sans text-xl font-semibold leading-[1.5] text-black">
                Finish creating your account
              </h1>
              <p className="font-open-sans text-sm font-normal leading-[1.5] text-black">Set a password below</p>
            </div>
          )}

          <div className="space-y-4">
            {!skipPassword && (
              <>
                {/* This is to help tools like 1password know what the email is */}
                <input
                  type="hidden"
                  id="username"
                  name="username"
                  autoComplete="username"
                  defaultValue={user?.email_address}
                />
                <div className="flex flex-col gap-2.5">
                  <label className="font-albert-sans text-label-gray text-sm font-medium leading-5">Password</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path
                          fill="#6a7282"
                          fillRule="evenodd"
                          d="M12 2C8.13 2 5 5.13 5 9c0 1.74.65 3.33 1.72 4.53L3.29 20.29a1 1 0 101.42 1.42l3.43-3.43A7.96 7.96 0 0012 16c3.87 0 7-3.13 7-7s-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <input
                      required
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`font-manrope focus:border-button-primary/10 focus:ring-button-primary/10 block w-full rounded-lg border bg-gray-50 p-4 !pl-12 text-sm text-gray-900 focus:border-opacity-10 focus:outline-none focus:ring-4 md:!bg-white md:!py-3 md:!pr-12 md:!text-base ${
                        passwordError !== '' ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                        {showPassword ? (
                          <>
                            <path
                              fill="currentColor"
                              d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                            />
                          </>
                        ) : (
                          <>
                            <path
                              fill="currentColor"
                              d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                            />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {passwordError !== '' && (
                    <p className="font-open-sans mt-1 text-xs font-normal leading-4 text-red-500">{passwordError}</p>
                  )}
                </div>
              </>
            )}
            {!skipPassword && (
              <div className="flex flex-col gap-2.5">
                <label className="font-albert-sans text-label-gray text-sm font-medium leading-5">
                  Re-enter Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <path
                        fill="#6a7282"
                        fillRule="evenodd"
                        d="M12 2C8.13 2 5 5.13 5 9c0 1.74.65 3.33 1.72 4.53L3.29 20.29a1 1 0 101.42 1.42l3.43-3.43A7.96 7.96 0 0012 16c3.87 0 7-3.13 7-7s-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    required
                    id="password2"
                    name="password2"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    placeholder="Re-enter your Password..."
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`font-manrope focus:border-button-primary/10 focus:ring-button-primary/10 block w-full rounded-lg border bg-gray-50 p-4 !pl-12 text-sm text-gray-900 focus:border-opacity-10 focus:outline-none focus:ring-4 md:!bg-white md:!py-3 md:!pr-12 md:!text-base ${
                      confirmPasswordError !== '' ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                      {showPasswordConfirm ? (
                        <>
                          <path
                            fill="currentColor"
                            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                          />
                        </>
                      ) : (
                        <>
                          <path
                            fill="currentColor"
                            d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                          />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {confirmPasswordError !== '' && (
                  <p className="font-open-sans mt-1 text-xs font-normal leading-4 text-red-500">
                    {confirmPasswordError}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-4">
              <label className="flex items-start gap-2">
                <Checkbox checked={termsConditionsChecked} onChange={handleCheckboxChange} />
                <span className="font-open-sans pt-1 text-sm font-normal leading-[1.5] text-black">
                  I have read and accept the{' '}
                  <button type="button" onClick={() => setModal('t&c')} className="text-button-primary hover:underline">
                    Terms & Conditions of Use
                  </button>{' '}
                  including the consent to receive electronic communications, and the{' '}
                  <button
                    type="button"
                    onClick={() => setModal('privacy')}
                    className="text-button-primary hover:underline"
                  >
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2">
                <Checkbox checked={messaging} onChange={() => setMessaging(!messaging)} />
                <span className="font-open-sans pt-1 text-sm font-normal leading-[1.5] text-black">
                  I agree to receive electronic communications from {theme.name} about my account, Peer connections,
                  service updates, and service highlights, and other {theme.name} news and information.
                </span>
              </label>
            </div>
            <Button
              type="submit"
              className="bg-button-primary w-full rounded-full px-7 py-[10px] disabled:opacity-50"
              variant="primary"
              disabled={
                skipPassword
                  ? !termsConditionsChecked || updateUser.isLoading
                  : passwordError !== '' ||
                    confirmPasswordError !== '' ||
                    !termsConditionsChecked ||
                    updateUser.isLoading
              }
              loading={updateUser.isLoading}
            >
              <span className="font-albert-sans text-base font-medium leading-5 text-white">Continue</span>
            </Button>
          </div>
        </div>
      </form>

      <Modal open={modal === 't&c'} onClose={() => setModal(undefined)} aria-labelledby="terms-modal-title">
        <div className="scrollable-height m-4 overflow-auto bg-white p-4">
          <div className="flex justify-end">
            <CloseIcon
              className="text-primary h-4 w-4 cursor-pointer"
              onClick={() => setModal(undefined)}
              title="Close Modal"
            />
          </div>
          <div
            className="prose prose-black prose-strong:font-bold max-w-none px-4 py-4"
            dangerouslySetInnerHTML={{
              __html: termsOfService?.text ?? '',
            }}
          ></div>
          <Button onClick={() => setModal(undefined)} className="my-4 w-full" variant="primary">
            Close
          </Button>
        </div>
      </Modal>

      <Modal open={modal === 'privacy'} onClose={() => setModal(undefined)} aria-labelledby="privacy-modal-title">
        <div className="scrollable-height m-4 overflow-auto bg-white p-4">
          <div className="flex justify-end">
            <CloseIcon
              className="text-primary h-4 w-4 cursor-pointer"
              onClick={() => setModal(undefined)}
              title="Close Modal"
            />
          </div>
          <div
            className="prose prose-black prose-strong:font-bold max-w-none px-4 py-4"
            dangerouslySetInnerHTML={{
              __html: privacyPolicy?.text ?? '',
            }}
          ></div>
          <Button onClick={() => setModal(undefined)} className="my-4 w-full" variant="primary">
            Close
          </Button>
        </div>
      </Modal>
    </OnboardingLayout>
  );
};
